import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Database, AlertCircle } from "lucide-react";
import LanguageToggle from "../components/LanguageToggle";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      toast.success(t("login.signed_in_success"));
      if (nextParam && nextParam.startsWith("/")) {
        navigate(nextParam);
      } else {
        navigate(result.user?.role === "sales_rep" ? "/sales-rep-home" : "/");
      }
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 relative" data-testid="login-page">
      {/* iter 178u: language toggle exposed on the login page too, so users
          without an account yet can flip UI language before signing in. */}
      <div className="absolute top-6 right-6">
        <LanguageToggle />
      </div>
      <div className="w-full max-w-sm bg-white border border-gray-200 rounded-2xl p-10 shadow-sm">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center">
            <Database size={18} className="text-brand-600" strokeWidth={1.75} />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-[15px] text-gray-900">{t("app_title")}</div>
            <div className="font-mono text-[9px] uppercase tracking-label text-gray-400">{t("login.subtitle")}</div>
          </div>
        </div>

        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">{t("login.sign_in")}</h1>
        <p className="mt-1.5 text-sm text-gray-500">{t("login.title")}</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-xs font-medium text-gray-600">{t("login.email")}</Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              required
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@chinainventory.com"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-medium text-gray-600">{t("login.password")}</Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              required
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <AlertCircle size={14} strokeWidth={1.75} /> {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full mt-2 rounded-lg bg-[#1677ff] hover:bg-[#0958d9] transition-colors h-10 text-sm font-medium"
          >
            {loading ? t("login.signing_in") : t("login.sign_in")}
          </Button>
        </form>
      </div>
    </div>
  );
}
